/**
 * Modular Build Configurator — v6 (Phase 5)
 * Luxe Patios | Haven x Coyote
 *
 * Step model — 4 MAIN steps with sub-steps:
 *   Step 1 — CABINET: size (1a) → finish (1b) → placement (1c, 96" only)
 *   Step 2 — GRILL:   fuel (2a, 96" only) → grill model (2b)
 *   Step 3 — CONFIG:  config (3a) → fridge model (3b, REF only)
 *   Step 4 — REVIEW (drawer expands into this)
 *
 * Sub-step navigation: Back/Continue moves through sub-steps within a main step,
 * then crosses into the previous/next main step. Top-bar dots = MAIN steps (4),
 * and clicking jumps to the FIRST sub-step of that main step, preserving state.
 *
 * State preserved on jump-back. Drawer is persistent on steps 1-3.
 * On step 4 (review): pill hidden, drawer is hidden too — the review page IS
 * the expanded drawer with sales-close framing.
 */

const ModularBuild = (() => {

  const KEY   = 'modular_build_v6';
  const DEBUG = true;

  function _readThemeImages() {
    const wrap = document.getElementById('modular-configurator');
    if (!wrap) return { plainPanel: '', placementLeft: '', placementRight: '' };
    return {
      plainPanel:     wrap.dataset.mbImagePlainPanel || '',
      placementLeft:  wrap.dataset.mbImagePlacementLeft || '',
      placementRight: wrap.dataset.mbImagePlacementRight || ''
    };
  }

  // ─── BOM ──────────────────────────────────────────────────────────────────
  const ISLAND_BOM = {
    HCOY72PAN:   ['C3-DA2431'],
    HCOY72TRASH: ['C3-DA2431', 'C3-STC'],
    HCOY72DRAW:  ['C3-DA2431', 'C3-2DC'],
    HCOY72REF:   ['C3-DA2431'],

    HCOY96PAN:   ['C3-DA2436', 'C3-STC'],
    HCOY96DRAW:  ['C3-DA2436', 'C3-STC', 'C3-2DC'],
    HCOY96REF:   ['C3-DA2436', 'C3-STC'],

    HCOY96PPAN:  ['C3-SSD36', 'C3-STC'],
    HCOY96PDRAW: ['C3-SSD36', 'C3-STC', 'C3-2DC'],
    HCOY96PREF:  ['C3-SSD36', 'C3-STC']
  };

  const COMPONENT_LABELS = {
    'C3-DA2431':  'Coyote 17" Access Door',
    'C3-DA2436':  'Coyote 21" Access Door',
    'C3-STC':     'Coyote Slide-In Trash Chute',
    'C3-2DC':     'Coyote 2-Drawer Cabinet',
    'C3-SSD36':   'Coyote Side Storage Door'
  };

  function _log(...args)  { if (DEBUG) console.log('[ModularBuild]', ...args); }
  function _warn(...args) { console.warn('[ModularBuild]', ...args); }

  function _findComponentBySku(sku) {
    if (!sku) return null;
    const el = document.querySelector(`[data-mb-component][data-sku="${CSS.escape(sku)}"]`);
    if (!el) return null;
    return {
      variantId: el.dataset.variantId,
      title:     el.dataset.title,
      price:     parseInt(el.dataset.price),
      image:     el.dataset.image,
      sku:       el.dataset.sku
    };
  }

  function _resolveComponents(islandSku) {
    if (!islandSku) return [];
    const prefix = String(islandSku).toUpperCase().replace(/(HG|BB|AB)$/, '');
    const bom    = ISLAND_BOM[prefix];
    if (!bom) {
      _warn(`No BOM defined for island prefix "${prefix}".`);
      return [];
    }
    const components = [];
    bom.forEach(sku => {
      const c = _findComponentBySku(sku);
      if (c) components.push(c);
      else   _warn(`Component SKU "${sku}" not found.`);
    });
    return components;
  }

  // ─── STEP MODEL ───────────────────────────────────────────────────────────
  /**
   * Each step is { main, sub, id, key, needs }.
   * `id` is the unique step identifier used by the JS + Liquid markup.
   * `key` is the state slot it sets.
   * `needs` is a fn(state) => bool — whether this sub-step applies for the
   * current state. Used to skip placement/fuel/fridge on irrelevant flows.
   */
  const STEPS = [
    { main: 1, sub: 'a', id: 'size',      key: 'size',      needs: () => true },
    { main: 1, sub: 'b', id: 'finish',    key: 'finish',    needs: () => true },
    { main: 1, sub: 'c', id: 'placement', key: 'placement', needs: s => s.size === '96' },
    { main: 2, sub: 'a', id: 'grillType', key: 'grillType', needs: s => s.size === '96' },
    { main: 2, sub: 'b', id: 'grill',     key: 'grill',     needs: () => true },
    { main: 3, sub: 'a', id: 'config',    key: 'config',    needs: () => true },
    { main: 3, sub: 'b', id: 'fridge',    key: 'fridge',    needs: s => s.config === 'REF' },
    { main: 4, sub: 'a', id: 'review',    key: null,        needs: () => true }
  ];

  const MAIN_LABELS = {
    1: 'Cabinet',
    2: 'Grill',
    3: 'Configuration',
    4: 'Review'
  };

  const SUB_LABELS = {
    size:      'Size',
    finish:    'Finish',
    placement: 'Placement',
    grillType: 'Fuel Type',
    grill:     'Grill Model',
    config:    'Components',
    fridge:    'Refrigerator',
    review:    'Review'
  };

  function activeSteps(s) {
    return STEPS.filter(step => step.needs(s));
  }

  function currentStepDef(s) {
    const active = activeSteps(s);
    return active.find(st => st.id === s.currentStepId) || active[0];
  }

  function nextStepId(s) {
    const active = activeSteps(s);
    const idx = active.findIndex(st => st.id === s.currentStepId);
    return idx >= 0 && idx < active.length - 1 ? active[idx + 1].id : null;
  }

  function prevStepId(s) {
    const active = activeSteps(s);
    const idx = active.findIndex(st => st.id === s.currentStepId);
    return idx > 0 ? active[idx - 1].id : null;
  }

  function firstStepIdInMain(s, mainNum) {
    const active = activeSteps(s);
    const step = active.find(st => st.main === mainNum);
    return step ? step.id : active[0].id;
  }

  /**
   * Has the user reached or passed `stepId`? Used for jump-back gating:
   * progress dots only become clickable for completed main steps.
   */
  function hasReached(s, stepId) {
    const active = activeSteps(s);
    const currentIdx = active.findIndex(st => st.id === s.currentStepId);
    const targetIdx  = active.findIndex(st => st.id === stepId);
    return targetIdx >= 0 && currentIdx >= 0 && targetIdx <= currentIdx;
  }

  function hasReachedMain(s, mainNum) {
    const active = activeSteps(s);
    const currentIdx = active.findIndex(st => st.id === s.currentStepId);
    if (currentIdx < 0) return false;
    const currentMain = active[currentIdx].main;
    return mainNum <= currentMain;
  }

  // ─── STATE ────────────────────────────────────────────────────────────────
  const blank = () => ({
    size:        null,
    finish:      null,
    placement:   null,
    grillType:   null,
    grill:       null,
    config:      null,
    fridge:      null,
    island:      null,
    islandUnresolved: false,
    currentStepId: 'size',
    cartKeys: []
  });

  function load() { try { const r = sessionStorage.getItem(KEY); return r ? JSON.parse(r) : blank(); } catch { return blank(); } }
  function save(patch) { const s = Object.assign(load(), patch); sessionStorage.setItem(KEY, JSON.stringify(s)); return s; }
  function reset() { sessionStorage.removeItem(KEY); }

  // ─── NAVIGATION ───────────────────────────────────────────────────────────
  function goToStepId(stepId) {
    const s = save({ currentStepId: stepId });
    _render(s);
    const cfg = document.getElementById('modular-configurator');
    if (cfg) window.scrollTo({ top: cfg.offsetTop - 80, behavior: 'smooth' });
  }

  function goToMainStep(mainNum) {
    const s = load();
    if (!hasReachedMain(s, mainNum)) return; // gate
    const targetId = firstStepIdInMain(s, mainNum);
    goToStepId(targetId);
  }

  function next() {
    const s = load();
    const n = nextStepId(s);
    if (n) goToStepId(n);
  }

  function back() {
    const s = load();
    const p = prevStepId(s);
    if (p) goToStepId(p);
  }

  // ─── SELECTIONS ───────────────────────────────────────────────────────────
  function pickSize(size) {
    const s = load();
    const patch = { size };
    // Clear downstream choices invalidated by a size change
    if (s.size && s.size !== size) {
      patch.placement = null;
      patch.grillType = null;
      patch.grill = null;
      patch.island = null;
      patch.islandUnresolved = false;
    }
    save(patch);
    _markSelected('[data-mb-size]', size);
    _enableNext();
    _render(load());
  }

  function pickFinish(finish) {
    save({ finish });
    _markSelected('[data-mb-finish]', finish);
    _resolveIsland(load());
    _enableNext();
    _render(load());
  }

  function pickPlacement(placement) {
    save({ placement });
    _markSelected('[data-mb-placement]', placement);
    _resolveIsland(load());
    _enableNext();
    _render(load());
  }

  function pickGrillType(grillType) {
    save({ grillType, grill: null });
    _markSelected('[data-mb-grilltype]', grillType);
    _enableNext();
    _render(load());
  }

  function pickGrill(data) {
    save({ grill: data });
    _markSelected('[data-mb-grill]', String(data.variantId));
    _enableNext();
    _render(load());
  }

  function pickGrillFromCard(el) {
    pickGrill({
      variantId: el.dataset.variantId,
      title:     el.dataset.title,
      price:     parseInt(el.dataset.price),
      image:     el.dataset.image
    });
  }

  function pickConfig(config) {
    const s = load();
    const patch = { config };
    if (s.config !== config) patch.fridge = null;
    save(patch);
    _markSelected('[data-mb-config]', config);
    _resolveIsland(load());
    _enableNext();
    _render(load());
  }

  function pickFridge(data) {
    save({ fridge: data });
    _markSelected('[data-mb-fridge]', String(data.variantId));
    _enableNext();
    _render(load());
  }

  function pickFridgeFromCard(el) {
    pickFridge({
      variantId: el.dataset.variantId,
      title:     el.dataset.title,
      price:     parseInt(el.dataset.price),
      image:     el.dataset.image
    });
  }

  // ─── REMOVE SELECTION (drawer × button) ──────────────────────────────────
  function removeSelection(stepKey) {
    const s = load();
    if (!s[stepKey]) {
      _warn(`removeSelection: no value at "${stepKey}".`);
      return;
    }
    const labels = {
      size: '6 ft / 8 ft size', finish: 'finish color',
      placement: 'grill placement', grillType: 'fuel type',
      grill: 'grill selection', config: 'configuration',
      fridge: 'refrigerator selection'
    };
    const cascadeWarn = ['size', 'finish', 'placement', 'grillType', 'config'].includes(stepKey)
      ? '\n\nThis will clear later choices that depended on it. You\'ll need to re-pick them.'
      : '';
    const ok = window.confirm(`Remove your ${labels[stepKey] || stepKey}?${cascadeWarn}`);
    if (!ok) return;

    let jumpTo = 'size';
    switch (stepKey) {
      case 'size':      save(blank()); jumpTo = 'size'; break;
      case 'finish':    save({ finish: null, island: null, islandUnresolved: false }); jumpTo = 'finish'; break;
      case 'placement': save({ placement: null, island: null, islandUnresolved: false }); jumpTo = 'placement'; break;
      case 'grillType': save({ grillType: null, grill: null }); jumpTo = 'grillType'; break;
      case 'grill':     save({ grill: null }); jumpTo = 'grill'; break;
      case 'config':    save({ config: null, fridge: null, island: null, islandUnresolved: false }); jumpTo = 'config'; break;
      case 'fridge':    save({ fridge: null }); jumpTo = 'fridge'; break;
    }
    _hideIslandPreview();
    goToStepId(jumpTo);
  }

  // ─── DRAWER ───────────────────────────────────────────────────────────────
  function openDrawer() {
    const drawer = document.getElementById('mb-drawer');
    const overlay = document.getElementById('mb-drawer-overlay');
    if (!drawer || !overlay) return;
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    const drawer = document.getElementById('mb-drawer');
    const overlay = document.getElementById('mb-drawer-overlay');
    if (!drawer || !overlay) return;
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
  }

  function toggleDrawer() {
    const drawer = document.getElementById('mb-drawer');
    if (!drawer) return;
    drawer.classList.contains('is-open') ? closeDrawer() : openDrawer();
  }

  function _renderDrawer(s) {
    s = s || load();
    const body         = document.getElementById('mb-drawer-body');
    const pillCount    = document.getElementById('mb-pill-count');
    const drawerCount  = document.getElementById('mb-drawer-count');
    const drawerFooter = document.getElementById('mb-drawer-footer');
    if (!body) return;

    const items = _buildDrawerItems(s);
    const count = items.length;

    if (pillCount)   pillCount.textContent = count;
    if (drawerCount) drawerCount.textContent = count === 1 ? '1 selection' : `${count} selections`;

    if (count === 0) {
      body.innerHTML = `<div class="mb-drawer-empty">No selections yet. Start by choosing your island size.</div>`;
    } else {
      body.innerHTML = items.map(item => `
        <div class="mb-drawer-item" data-mb-drawer-step="${item.stepKey}">
          <div class="mb-drawer-item__thumb">
            ${item.image
              ? `<img src="${item.image}" alt="${_esc(item.title)}" loading="lazy">`
              : `<div class="mb-drawer-item__thumb-fallback">${item.fallback || ''}</div>`}
          </div>
          <div class="mb-drawer-item__info">
            <div class="mb-drawer-item__label">${_esc(item.label)}</div>
            <div class="mb-drawer-item__title">${_esc(item.title)}</div>
            ${item.price != null ? `<div class="mb-drawer-item__price">${_money(item.price)}</div>` : ''}
          </div>
          <button type="button" class="mb-drawer-item__remove" aria-label="Remove ${_esc(item.label)}" data-mb-remove="${item.stepKey}">×</button>
        </div>
      `).join('');

      body.querySelectorAll('[data-mb-remove]').forEach(btn => {
        btn.addEventListener('click', e => {
          e.stopPropagation();
          removeSelection(btn.dataset.mbRemove);
        });
      });
    }

    if (drawerFooter) {
      const currentDef = currentStepDef(s);
      const onReview = currentDef && currentDef.id === 'review';
      drawerFooter.innerHTML = onReview
        ? `<button type="button" class="mb-btn mb-btn--gold mb-btn--block" onclick="ModularBuild.closeDrawer(); ModularBuild.addToCart();">Add to Cart →</button>`
        : `<button type="button" class="mb-btn mb-btn--gold mb-btn--block" onclick="ModularBuild.closeDrawer(); ModularBuild.goToStepId('review');"${count === 0 ? ' disabled' : ''}>Review &amp; Continue →</button>`;
    }
  }

  function _buildDrawerItems(s) {
    const items = [];
    const configLabels    = { PAN: 'Plain Panel', TRASH: 'Trash Center', DRAW: '2 Drawer Cabinet', REF: '24" Refrigerator Space' };
    const finishLabels    = { HG: 'Harbor Gray', BB: 'Brushed Black', AB: 'Antique Bronze' };
    const placementLabels = { L: 'Left-Side Grill', R: 'Right-Side Grill' };
    const grillTypeLabels = { standard: 'Gas Grill', pellet: 'Pellet Grill' };
    const themeImages = _readThemeImages();

    if (s.size) {
      items.push({
        stepKey: 'size',
        label:   'Island Size',
        title:   s.size === '72' ? '6 ft Island (72")' : '8 ft Island (96")',
        fallback: s.size === '72' ? '72"' : '96"'
      });
    }
    if (s.finish) {
      const image = s.island ? s.island.image : '';
      items.push({ stepKey: 'finish', label: 'Finish', title: finishLabels[s.finish] || s.finish, image, fallback: s.finish });
    }
    if (s.placement && s.size === '96') {
      items.push({ stepKey: 'placement', label: 'Placement', title: placementLabels[s.placement], fallback: s.placement });
    }
    if (s.grillType && s.size === '96') {
      items.push({ stepKey: 'grillType', label: 'Fuel Type', title: grillTypeLabels[s.grillType], fallback: s.grillType === 'pellet' ? '🪵' : '🔥' });
    }
    if (s.grill) {
      items.push({ stepKey: 'grill', label: 'Grill', title: s.grill.title, price: s.grill.price, image: s.grill.image });
    }
    if (s.config) {
      let image = '';
      if (s.config === 'PAN' && themeImages.plainPanel) image = themeImages.plainPanel;
      else if (s.config === 'TRASH') { const c = _findComponentBySku('C3-STC'); if (c) image = c.image; }
      else if (s.config === 'DRAW')  { const c = _findComponentBySku('C3-2DC'); if (c) image = c.image; }
      else if (s.config === 'REF') {
        const firstFridge = document.querySelector('[data-mb-fridge]');
        if (firstFridge) image = firstFridge.dataset.image;
      }
      items.push({ stepKey: 'config', label: 'Configuration', title: configLabels[s.config] || s.config, image });
    }
    if (s.fridge) {
      items.push({ stepKey: 'fridge', label: 'Refrigerator', title: s.fridge.title, price: s.fridge.price, image: s.fridge.image });
    }
    return items;
  }

  function _esc(str) {
    return String(str || '').replace(/[&<>"']/g, m => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[m]));
  }

  // ─── ISLAND RESOLUTION ────────────────────────────────────────────────────
  function _parseSku(sku) {
    if (!sku) return null;
    const re = /^HCOY(72|96)(P?)(PAN|TRASH|DRAW|REF)(HG|BB|AB)$/i;
    const m = String(sku).trim().toUpperCase().match(re);
    if (!m) return null;
    return {
      size: m[1], grilltype: m[2] === 'P' ? 'pellet' : 'standard',
      config: m[3], finish: m[4]
    };
  }

  function _readIslandAttrs(card) {
    const meta = {
      size:      (card.dataset.mbSize      || '').trim(),
      config:    (card.dataset.mbConfig    || '').trim(),
      placement: (card.dataset.mbPlacement || '').trim(),
      grilltype: (card.dataset.mbGrilltype || '').trim(),
      finish:    (card.dataset.mbFinish    || '').trim()
    };
    const parsed = _parseSku(card.dataset.sku);
    return {
      size:      meta.size      || (parsed ? parsed.size      : ''),
      config:    meta.config    || (parsed ? parsed.config    : ''),
      placement: meta.placement,
      grilltype: meta.grilltype || (parsed ? parsed.grilltype : ''),
      finish:    meta.finish    || (parsed ? parsed.finish    : '')
    };
  }

  function _resolveIsland(s) {
    if (!s.size || !s.finish) return;
    // Config might not be picked yet — that's fine, we try to resolve once it is.
    if (!s.config) return;
    if (s.size === '96' && (!s.placement || !s.grillType)) return;

    const cards = document.querySelectorAll('[data-mb-island]');
    if (cards.length === 0) { save({ island: null, islandUnresolved: true }); return; }

    let matched = null;
    cards.forEach(card => {
      const attrs = _readIslandAttrs(card);
      const sizeMatch   = attrs.size === s.size;
      const configMatch = attrs.config === s.config;
      const finishMatch = attrs.finish === s.finish;
      const placementMatch =
        s.size !== '96'        ? true :
        attrs.placement === '' ? true :
        attrs.placement === s.placement;
      const grillTypeMatch =
        s.size !== '96'        ? true :
        attrs.grilltype === '' ? true :
        attrs.grilltype === s.grillType;

      if (sizeMatch && configMatch && finishMatch && placementMatch && grillTypeMatch && !matched) {
        matched = card;
      }
    });

    if (matched) {
      const island = {
        variantId: matched.dataset.variantId,
        productId: matched.dataset.productId,
        title:     matched.dataset.title,
        price:     parseInt(matched.dataset.price),
        image:     matched.dataset.image,
        sku:       matched.dataset.sku
      };
      save({ island, islandUnresolved: false });
      _updateIslandPreview(island);
    } else {
      save({ island: null, islandUnresolved: true });
      _hideIslandPreview();
    }
  }

  function _updateIslandPreview(island) {
    const el = document.getElementById('mb-island-preview');
    if (!el || !island) return;
    let imageUrl = island.image || '';
    if (imageUrl.startsWith('//')) imageUrl = 'https:' + imageUrl;
    imageUrl = imageUrl.replace(/(\?|&)width=\d+/, '$1width=1200');

    el.innerHTML = `
      ${imageUrl ? `<img src="${imageUrl}" alt="${_esc(island.title)}" loading="lazy">` : ''}
      <div class="mb-island-preview__info">
        <span class="mb-island-preview__label">Your Island</span>
        <h4>${_esc(island.title)}</h4>
        ${island.sku ? `<p class="mb-island-preview__sku">SKU: ${_esc(island.sku)}</p>` : ''}
        <p class="mb-price">${_money(island.price)}</p>
      </div>
    `;
    el.hidden = false;
  }

  function _hideIslandPreview() {
    const el = document.getElementById('mb-island-preview');
    if (el) el.hidden = true;
  }

  // ─── REVIEW ───────────────────────────────────────────────────────────────
  function _buildReview() {
    const s   = load();
    const box = document.getElementById('mb-review-items');
    if (!box) return;

    if (!s.island && s.size && s.config && s.finish) {
      _renderDataGapFallback(box, s);
      const cartBtn = document.getElementById('mb-cart-btn');
      if (cartBtn) {
        cartBtn.disabled = true;
        cartBtn.classList.add('is-disabled');
        cartBtn.style.display = 'none';
      }
      _renderReviewConfigSummary(s);
      return;
    }

    let total = 0;
    let html  = '';

    const primaryRow = (label, item, jumpStepId) => {
      if (!item) return '';
      total += Number(item.price);
      return `
        <div class="mb-review-row">
          <div class="mb-review-row__img-wrap">
            ${item.image
              ? `<img src="${item.image}" alt="${_esc(item.title)}" loading="lazy">`
              : `<span class="mb-review-row__img-wrap-empty">⊟</span>`}
          </div>
          <div>
            <span class="mb-review-row__label">${_esc(label)}</span>
            <p class="mb-review-row__title">${_esc(item.title)}</p>
            ${item.sku ? `<span class="mb-review-row__sku">SKU: ${_esc(item.sku)}</span>` : ''}
          </div>
          <div class="mb-review-row__price">${_money(Number(item.price))}</div>
          <button class="mb-review-row__edit" onclick="ModularBuild.goToStepId('${jumpStepId}')">Edit</button>
        </div>`;
    };

    const componentRow = (component) => {
      if (!component) return '';
      total += Number(component.price);
      const niceName = COMPONENT_LABELS[component.sku] || component.title;
      return `
        <div class="mb-review-component-row">
          <div class="mb-review-row__img-wrap mb-review-row__img-wrap--sm">
            ${component.image
              ? `<img src="${component.image}" alt="${_esc(niceName)}" loading="lazy">`
              : `<span class="mb-review-row__img-wrap-empty">↳</span>`}
          </div>
          <div>
            <p class="mb-review-component-row__title">${_esc(niceName)}</p>
            <p class="mb-review-component-row__sku">SKU: ${_esc(component.sku)}</p>
          </div>
          <div class="mb-review-component-row__price">${_money(Number(component.price))}</div>
        </div>`;
    };

    if (s.island) {
      html += primaryRow('Kitchen Island', s.island, 'finish');
      const components = _resolveComponents(s.island.sku);
      if (components.length > 0) {
        html += '<div class="mb-review-components-group">';
        html += '<p class="mb-review-components-group__label">Included with your island</p>';
        components.forEach(c => { html += componentRow(c); });
        html += '</div>';
      }
    }
    if (s.grill)  html += primaryRow('Coyote Grill', s.grill,  'grill');
    if (s.fridge) html += primaryRow('Refrigerator', s.fridge, 'fridge');

    if (!html) html = '<p class="mb-review__loading">No items selected yet.</p>';

    box.innerHTML = html;

    const totalEl = document.getElementById('mb-review-total');
    if (totalEl) {
      totalEl.innerHTML = `
        <span class="mb-review__total-label">Estimated Total</span>
        <span class="mb-review__total-price">${_money(total)}</span>
      `;
    }
    const discEl = document.getElementById('mb-review-disclaimer');
    if (discEl) {
      discEl.innerHTML = `All components above ship together to complete your build. Freight shipping calculated at checkout. Regional installation support available — ask us.`;
    }

    const cartBtn = document.getElementById('mb-cart-btn');
    if (cartBtn) {
      cartBtn.disabled = false;
      cartBtn.classList.remove('is-disabled');
      cartBtn.style.display = '';
      cartBtn.hidden = false;
    }

    _renderReviewConfigSummary(s);
  }

  function _renderReviewConfigSummary(s) {
    const summaryEl = document.getElementById('mb-review-config-summary');
    if (!summaryEl) return;
    const configLabels = { PAN: 'Plain Panel', TRASH: 'Trash Center', DRAW: '2 Drawer', REF: 'w/ Fridge' };
    const finishLabels = { HG: 'Harbor Gray', BB: 'Brushed Black', AB: 'Antique Bronze' };
    summaryEl.innerHTML = [
      s.size     ? `<span class="mb-chip">${s.size === '72' ? '6 ft' : '8 ft'}</span>` : '',
      s.config   ? `<span class="mb-chip">${configLabels[s.config] || s.config}</span>` : '',
      s.placement && s.size === '96' ? `<span class="mb-chip">${s.placement === 'R' ? 'Right Grill' : 'Left Grill'}</span>` : '',
      s.grillType && s.size === '96' ? `<span class="mb-chip">${s.grillType === 'pellet' ? 'Pellet' : 'Gas'}</span>` : '',
      s.finish   ? `<span class="mb-chip">${finishLabels[s.finish] || s.finish}</span>` : ''
    ].filter(Boolean).join('');
  }

  function _renderDataGapFallback(box, s) {
    const configLabels = { PAN: 'Plain Panel', TRASH: 'Trash Center', DRAW: '2 Drawer Cabinet', REF: '24" Refrigerator Space' };
    const finishLabels = { HG: 'Harbor Gray', BB: 'Brushed Black', AB: 'Antique Bronze' };
    const sizeLabel    = s.size === '72' ? '6 ft (72")' : '8 ft (96")';
    const configLabel  = configLabels[s.config] || s.config;
    const finishLabel  = finishLabels[s.finish] || s.finish;

    box.innerHTML = `
      <div class="mb-review-gap">
        <h3>Let's Complete This Build Together</h3>
        <p>Your combination — <strong>${sizeLabel} ${configLabel} in ${finishLabel}</strong> — needs a quick confirmation from our Design Studio before we add it to your cart.</p>
        <div class="mb-review-gap__actions">
          <a href="/pages/contact" class="mb-btn mb-btn--gold">Contact Our Team</a>
          <a href="tel:+18777475893" class="mb-btn mb-btn--ghost">Call (877) 747-5893</a>
        </div>
        ${s.grill ? `
          <p class="mb-review-gap__partial-note">You can also continue and we'll add your grill${s.fridge ? ' and refrigerator' : ''} now — we'll follow up about the island.</p>
          <button type="button" id="mb-cart-partial-btn" class="mb-btn mb-btn--dark">Add Grill${s.fridge ? ' &amp; Fridge' : ''} Only</button>
        ` : ''}
      </div>
    `;

    const partialBtn = document.getElementById('mb-cart-partial-btn');
    if (partialBtn) partialBtn.addEventListener('click', () => addToCart({ allowPartial: true }));
  }

  // ─── CART ─────────────────────────────────────────────────────────────────
  async function addToCart(opts) {
    opts = opts || {};
    const s   = load();
    const btn = document.getElementById('mb-cart-btn');

    if (!s.grill) { _showMsg('Please select a grill before adding to cart.', 'error'); return; }
    if (!s.island && !opts.allowPartial) {
      _showMsg('We could not finalize your island selection. Please contact us to complete this build.', 'error');
      return;
    }

    const items = [];
    if (s.island) {
      items.push({ id: Number(s.island.variantId), quantity: 1 });
      const components = _resolveComponents(s.island.sku);
      components.forEach(c => items.push({ id: Number(c.variantId), quantity: 1 }));
    }
    items.push({ id: Number(s.grill.variantId), quantity: 1 });
    if (s.fridge) items.push({ id: Number(s.fridge.variantId), quantity: 1 });

    if (btn) { btn.disabled = true; btn.textContent = 'Adding to Cart…'; }

    try {
      const res = await fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ items })
      });
      if (!res.ok) throw new Error('Cart API failed: ' + res.status);
      const data = await res.json();
      save({ cartKeys: (data.items || []).map(i => i.key) });
      await _refreshCartBubble();
      _showSuccess(opts.allowPartial && !s.island);
      reset();
    } catch (e) {
      console.error('[ModularBuild] cart error', e);
      _showMsg('Something went wrong — please try again or contact us.', 'error');
      if (btn) { btn.disabled = false; btn.textContent = 'Add Complete Build to Cart'; }
    }
  }

  async function _refreshCartBubble() {
    try {
      const r    = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await r.json();
      document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(el => el.textContent = cart.item_count);
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch {}
  }

  // ─── RENDER ──────────────────────────────────────────────────────────────
  function _render(s) {
    s = s || load();
    const currentDef = currentStepDef(s);
    if (!currentDef) return;

    // Show only the active sub-step section
    document.querySelectorAll('[data-mb-substep]').forEach(el => {
      const match = el.dataset.mbSubstep === currentDef.id;
      el.hidden = !match;
      el.setAttribute('aria-hidden', String(!match));
    });

    // Special: review step
    const isReview = currentDef.id === 'review';

    // Step 6 (grill) — filter
    if (currentDef.id === 'grill') _filterGrillCards(s);

    // Step 2 config — inject photos
    if (currentDef.id === 'config') _injectConfigPhotos();

    // Step 4a — placement: inject photos
    if (currentDef.id === 'placement') _injectPlacementPhotos();

    // Step 2a — fuel type: inject real grill product images
    if (currentDef.id === 'grillType') _injectGrillTypePhotos();

    // Review — build the review content
    if (isReview) _buildReview();

    _syncSelections(currentDef.id, s);
    _renderTopbar(s, currentDef);
    _renderSubBreadcrumb(s, currentDef);
    _setNextState(currentDef, s);
    _renderCascadeAlerts(s, currentDef);
    _renderDrawer(s);

    // Back/Continue/Cart visibility
    const nextBtn = document.getElementById('mb-next-btn');
    const backBtn = document.getElementById('mb-back-btn');
    const cartBtn = document.getElementById('mb-cart-btn');
    const pillEl  = document.getElementById('mb-pill');

    if (backBtn) backBtn.hidden = !prevStepId(s);
    if (nextBtn) nextBtn.hidden = isReview;
    if (cartBtn) cartBtn.hidden = !isReview;

    // Pill is hidden on review (drawer "is" the page now)
    if (pillEl) pillEl.hidden = isReview;

    // Review-expand hook: when on review, close drawer if open
    if (isReview) closeDrawer();

    _showMsg('', '');
  }

  function _renderTopbar(s, currentDef) {
    const wrap = document.getElementById('mb-topbar-progress');
    if (!wrap) return;

    const mains = [1, 2, 3, 4];
    wrap.innerHTML = mains.map(m => {
      const isCurrent  = currentDef.main === m;
      const isComplete = currentDef.main > m;
      const isReached  = hasReachedMain(s, m);
      const cls = [
        'mb-prog-item',
        isCurrent  ? 'is-active'   : '',
        isComplete ? 'is-complete' : '',
        isReached  ? 'is-reached'  : ''
      ].filter(Boolean).join(' ');
      return `
        <button type="button" class="${cls}" data-mb-jump-main="${m}"${isReached ? '' : ' disabled'}>
          <span class="mb-prog-dot">${m}</span>
          <span class="mb-prog-label">${MAIN_LABELS[m]}</span>
        </button>`;
    }).join('');

    wrap.querySelectorAll('[data-mb-jump-main]').forEach(btn => {
      if (btn.disabled) return;
      btn.addEventListener('click', () => goToMainStep(parseInt(btn.dataset.mbJumpMain)));
    });

    // Counter
    const counter = document.getElementById('mb-step-counter');
    if (counter) counter.textContent = `${currentDef.main} of 4`;
  }

  function _renderSubBreadcrumb(s, currentDef) {
    const wrap = document.getElementById('mb-sub-breadcrumb');
    if (!wrap) return;

    const subs = activeSteps(s).filter(st => st.main === currentDef.main);
    if (subs.length <= 1) { wrap.hidden = true; return; }

    wrap.hidden = false;
    wrap.innerHTML = subs.map((st, i) => {
      const isCurrent  = st.id === currentDef.id;
      const isComplete = st.id !== currentDef.id && hasReached(s, st.id) && _hasValueForKey(s, st.key);
      const cls = [
        'mb-sub-crumb',
        isCurrent  ? 'is-active'   : '',
        isComplete ? 'is-complete' : ''
      ].filter(Boolean).join(' ');
      const sep = i < subs.length - 1 ? '<span class="mb-sub-crumb-sep">·</span>' : '';
      return `<span class="${cls}">${SUB_LABELS[st.id] || st.id}</span>${sep}`;
    }).join('');
  }

  function _hasValueForKey(s, key) {
    if (!key) return false;
    return s[key] != null && s[key] !== '';
  }

  function _setNextState(currentDef, s) {
    const btn = document.getElementById('mb-next-btn');
    if (!btn) return;
    if (currentDef.id === 'review') { btn.disabled = true; btn.classList.add('is-disabled'); return; }
    const hasValue = currentDef.key ? _hasValueForKey(s, currentDef.key) : true;
    btn.disabled = !hasValue;
    btn.classList.toggle('is-disabled', !hasValue);
  }

  function _enableNext() {
    const s = load();
    const def = currentStepDef(s);
    if (def) _setNextState(def, s);
  }

  function _markSelected(selector, value) {
    document.querySelectorAll(selector).forEach(el => {
      const active = el.dataset.value === String(value) || el.dataset.variantId === String(value);
      el.classList.toggle('is-selected', active);
    });
  }

  function _syncSelections(stepId, s) {
    if (stepId === 'size'      && s.size)      _markSelected('[data-mb-size]',      s.size);
    if (stepId === 'finish'    && s.finish)    { _markSelected('[data-mb-finish]', s.finish); if (s.island) _updateIslandPreview(s.island); }
    if (stepId === 'placement' && s.placement) _markSelected('[data-mb-placement]', s.placement);
    if (stepId === 'grillType' && s.grillType) _markSelected('[data-mb-grilltype]', s.grillType);
    if (stepId === 'grill'     && s.grill)     _markSelected('[data-mb-grill]',     String(s.grill.variantId));
    if (stepId === 'config'    && s.config)    _markSelected('[data-mb-config]',    s.config);
    if (stepId === 'fridge'    && s.fridge)    _markSelected('[data-mb-fridge]',    String(s.fridge.variantId));
  }

  // ─── CASCADE ALERTS ──────────────────────────────────────────────────────
  function _renderCascadeAlerts(s, currentDef) {
    const stepId = currentDef.id;
    const alertEl = document.getElementById('mb-cascade-alert');
    const alertBody = document.getElementById('mb-cascade-alert-body');
    if (!alertEl || !alertBody) return;

    let msg = '';
    if (stepId === 'size') {
      if (s.size === '72')      msg = `<strong>6 ft islands skip placement and fuel choice.</strong> You'll get a 30" gas grill with left-side placement standard.`;
      else if (s.size === '96') msg = `<strong>8 ft islands unlock placement and fuel type next.</strong> You'll also pick a 36" grill.`;
    } else if (stepId === 'config') {
      if (s.size === '96')      msg = `<strong>Trash Center isn't shown for 8 ft islands</strong> — it's already built in. The other three configurations work.`;
      else if (s.size === '72') msg = `<strong>All four configurations work with 6 ft islands.</strong> The Refrigerator option adds a fridge pick.`;
    }

    if (msg) {
      alertEl.hidden = false;
      alertBody.innerHTML = msg;
    } else {
      alertEl.hidden = true;
    }
  }

  // ─── PHOTO INJECTORS ─────────────────────────────────────────────────────
  function _injectConfigPhotos() {
    const themeImages = _readThemeImages();
    const CONFIG_TO_SKU = { PAN: null, TRASH: 'C3-STC', DRAW: 'C3-2DC', REF: null };

    document.querySelectorAll('[data-mb-config]').forEach(card => {
      const photoWrap = card.querySelector('.mb-card__image');
      if (!photoWrap || photoWrap.querySelector('img')) return;

      const config = card.dataset.value;
      const sku = CONFIG_TO_SKU[config];
      let imageUrl = '';
      let altText  = '';

      if (config === 'PAN' && themeImages.plainPanel) {
        imageUrl = themeImages.plainPanel;
        altText = 'Plain panel configuration';
      } else if (sku) {
        const comp = _findComponentBySku(sku);
        if (comp && comp.image) { imageUrl = comp.image; altText = comp.title; }
      } else if (config === 'REF') {
        const firstFridge = document.querySelector('[data-mb-fridge]');
        if (firstFridge && firstFridge.dataset.image) {
          imageUrl = firstFridge.dataset.image;
          altText = '24" outdoor refrigerator';
        }
      }

      if (imageUrl) {
        photoWrap.innerHTML = '';
        const img = document.createElement('img');
        img.src = imageUrl; img.alt = altText; img.loading = 'lazy';
        photoWrap.appendChild(img);
      }
    });
  }

  function _injectPlacementPhotos() {
    const themeImages = _readThemeImages();
    document.querySelectorAll('[data-mb-placement]').forEach(card => {
      const photoWrap = card.querySelector('.mb-card__image');
      if (!photoWrap || photoWrap.querySelector('img')) return;

      const placement = card.dataset.value;
      const url = placement === 'L' ? themeImages.placementLeft : themeImages.placementRight;
      if (url) {
        photoWrap.innerHTML = '';
        const img = document.createElement('img');
        img.src = url;
        img.alt = placement === 'L' ? 'Left-side grill placement' : 'Right-side grill placement';
        img.loading = 'lazy';
        photoWrap.appendChild(img);
      }
    });
  }

  /**
   * Step 2a fuel-type photo injector — replaces 🔥/🪵 emojis with the first
   * available product image from the grill collection that matches each fuel
   * type. This way "Gas Grill" shows a real gas grill photo and "Pellet Grill"
   * shows a real pellet grill photo. Falls back to emoji if no product found.
   */
  function _injectGrillTypePhotos() {
    document.querySelectorAll('[data-mb-grilltype]').forEach(card => {
      const photoWrap = card.querySelector('.mb-card__image');
      if (!photoWrap || photoWrap.querySelector('img')) return;

      const fuelType = card.dataset.value; // 'standard' or 'pellet'

      // Find the first grill in the data layer that matches this fuel type
      const grillCards = document.querySelectorAll('[data-mb-grill]');
      let matchedImage = '';
      let matchedTitle = '';

      grillCards.forEach(g => {
        if (matchedImage) return;
        let cardType = String(g.dataset.mbGrillType || '').trim();
        if (!cardType) {
          cardType = /pellet/i.test(g.dataset.title || '') ? 'pellet' : 'standard';
        }
        if (cardType === fuelType && g.dataset.image) {
          matchedImage = g.dataset.image;
          matchedTitle = g.dataset.title || '';
        }
      });

      if (matchedImage) {
        photoWrap.innerHTML = '';
        const img = document.createElement('img');
        img.src = matchedImage;
        img.alt = matchedTitle || (fuelType === 'pellet' ? 'Pellet grill' : 'Gas grill');
        img.loading = 'lazy';
        photoWrap.appendChild(img);
      }
      // else leave the emoji icon
    });
  }

  // ─── GRILL FILTERING ──────────────────────────────────────────────────────
  function _filterGrillCards(s) {
    const sizeMap     = { '72': '30', '96': '36' };
    const targetSize  = sizeMap[s.size] || '';
    const targetType  = s.size === '96' ? (s.grillType || '') : '';

    const allCards = document.querySelectorAll('[data-mb-grill]');
    let visible = 0;

    allCards.forEach(card => {
      let cardSize = String(card.dataset.mbGrillSize || '').trim();
      let cardType = String(card.dataset.mbGrillType || '').trim();
      if (!cardSize) {
        const titleMatch = String(card.dataset.title || '').match(/\b(30|36)["\u201D]?\b/);
        cardSize = titleMatch ? titleMatch[1] : '';
      }
      if (!cardType) cardType = /pellet/i.test(card.dataset.title || '') ? 'pellet' : 'standard';

      const sizeMatch = !targetSize || cardSize === targetSize;
      const typeMatch = !targetType || cardType === targetType;
      const show = sizeMatch && typeMatch;
      card.hidden = !show;
      card.style.display = show ? '' : 'none';
      if (show) visible++;
    });

    const grid = document.querySelector('[data-mb-substep="grill"] .mb-grid');
    let existingEmpty = grid?.querySelector('.mb-empty-injected');
    if (visible === 0 && grid && !existingEmpty) {
      const empty = document.createElement('p');
      empty.className = 'mb-empty mb-empty-injected';
      empty.innerHTML = `No matching ${targetType === 'pellet' ? 'pellet' : 'gas'} grills available for your ${s.size === '72' ? '6 ft' : '8 ft'} build. <a href="/pages/contact">Contact us</a>.`;
      grid.appendChild(empty);
    } else if (visible > 0 && existingEmpty) {
      existingEmpty.remove();
    }
  }

  function _money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(cents / 100);
  }

  function _showMsg(msg, type) {
    const el = document.getElementById('mb-msg');
    if (!el) return;
    el.textContent = msg;
    el.className   = `mb-msg${type ? ' mb-msg--' + type : ''}`;
    el.style.display = msg ? 'block' : 'none';
  }

  function _showSuccess(partial) {
    const cfg = document.getElementById('modular-configurator');
    if (!cfg) return;
    cfg.innerHTML = `
      <div class="mb-success">
        <div class="mb-success__check">✓</div>
        <h2>${partial ? 'Added to Your Cart' : 'Your Build Is in Your Cart'}</h2>
        <p>${partial ? 'Your grill has been added. Our Design Studio will follow up about your island within one business day.' : 'Your island, grill, and refrigerator have been added. Review your order at checkout.'}</p>
        <div class="mb-success__actions">
          <a href="/cart" class="mb-btn mb-btn--gold">Review Cart &amp; Checkout</a>
          <a href="/collections/all" class="mb-btn mb-btn--ghost">Continue Browsing</a>
        </div>
      </div>`;
  }

  // ─── COMPONENT FALLBACK FETCH ────────────────────────────────────────────
  async function _fetchComponentsFallback(handle) {
    handle = handle || 'modular-components';
    try {
      const r = await fetch(`/collections/${handle}/products.json?limit=250`);
      if (!r.ok) return 0;
      const data = await r.json();
      const products = data.products || [];

      let container = document.getElementById('mb-component-data');
      if (!container) {
        container = document.createElement('div');
        container.id = 'mb-component-data';
        container.style.display = 'none';
        document.body.appendChild(container);
      }

      products.forEach(p => {
        const variant = (p.variants && p.variants[0]) || {};
        const img = (p.images && p.images[0] && p.images[0].src) || '';
        const div = document.createElement('div');
        div.setAttribute('data-mb-component', '');
        div.setAttribute('data-sku', variant.sku || '');
        div.setAttribute('data-variant-id', variant.id || '');
        div.setAttribute('data-title', p.title || '');
        const priceCents = Math.round(parseFloat(variant.price || '0') * 100);
        div.setAttribute('data-price', String(priceCents));
        div.setAttribute('data-image', img);
        container.appendChild(div);
      });
      return products.length;
    } catch {
      return 0;
    }
  }

  // ─── INIT ────────────────────────────────────────────────────────────────
  function init() {
    const cfg = document.getElementById('modular-configurator');
    if (!cfg) return;

    const s = load();
    let compCount = document.querySelectorAll('[data-mb-component]').length;

    if (compCount === 0) {
      _fetchComponentsFallback('modular-components').then(() => {
        const def = currentStepDef(load());
        if (def && def.id === 'config') _injectConfigPhotos();
        if (def && def.id === 'review') _buildReview();
        _renderDrawer(load());
      });
    }

    _render(s);

    document.getElementById('mb-next-btn')?.addEventListener('click', next);
    document.getElementById('mb-back-btn')?.addEventListener('click', back);
    document.getElementById('mb-cart-btn')?.addEventListener('click', () => addToCart());
    document.getElementById('mb-restart-btn')?.addEventListener('click', () => { reset(); goToStepId('size'); });

    document.getElementById('mb-pill')?.addEventListener('click', openDrawer);
    document.getElementById('mb-drawer-close')?.addEventListener('click', closeDrawer);
    document.getElementById('mb-drawer-overlay')?.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', e => {
      if (e.key === 'Escape') {
        const drawer = document.getElementById('mb-drawer');
        if (drawer && drawer.classList.contains('is-open')) closeDrawer();
      }
    });
  }

  return {
    init,
    goToStepId, goToMainStep, next, back,
    pickSize, pickFinish, pickPlacement, pickGrillType,
    pickGrill, pickGrillFromCard,
    pickConfig, pickFridge, pickFridgeFromCard,
    addToCart, reset,
    removeSelection, openDrawer, closeDrawer, toggleDrawer
  };

})();

document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', ModularBuild.init)
  : ModularBuild.init();
